import { injectAsyncReducer } from '../../hermes-redux';
import USER_ROLES from 'constants/user-roles.constant';

import type { Restrictions, ShouldGrantAccessFunction } from 'router/types';

import { WpoAppointmentsRoutes } from './state/constants';

type FlowRoutesConfig = {
  urlPrefix: string;
  routeNames: Record<keyof typeof WpoAppointmentsRoutes, string>;
  shouldGrantAccess: ShouldGrantAccessFunction;
  // The array form is what the counselling routes have always passed; it is a
  // no-op in `applyRestrictions`, so access there rests on `shouldGrantAccess`.
  restrictions: Restrictions | string[];
  introTitleKey: string;
};

const createResolver = pageImport =>
  Promise.all([
    pageImport,
    import(
      /* webpackChunkName: "wpo-appointments-reducer" */ './state/reducer'
    ),
  ]).then(([component, reducer]) => {
    injectAsyncReducer('wpoAppointments', reducer.default);
    return component;
  });

// The containers work out which flow they are in from the URL (see
// `state/flow.tsx`). The `import()` calls stay as static literals so both
// route sets share one chunk per step.
export const createFlowRoutes = ({
  urlPrefix,
  routeNames,
  shouldGrantAccess,
  restrictions,
  introTitleKey,
}: FlowRoutesConfig) => {
  const route = (
    name: string,
    path: string,
    title: string,
    resolver: () => Promise<unknown>,
  ) => ({
    name,
    url: `${urlPrefix}/${path}`,
    resolver,
    shouldGrantAccess,
    restrictions,
    data: {
      title,
      includeInMenu: false,
      hideFooter: true,
      authorizedRoles: [USER_ROLES.employee],
    },
  });

  return {
    intro: route(routeNames.INTRO, 'intro', introTitleKey, () =>
      createResolver(
        import(
          /* webpackChunkName: "wpo-appointments-intro" */ './containers/intro/intro'
        ),
      ),
    ),
    contactDetails: route(
      routeNames.CONTACT_DETAILS,
      'contact-details',
      'appointments.contact_details.title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-contact-details" */ './containers/contact-details/contact-details'
          ),
        ),
    ),
    areaOfConcern: route(
      routeNames.AREA_OF_CONCERN,
      'area-of-concern',
      'appointments.wpo_area_of_concern.title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-area-of-concern" */ './containers/area-of-concern/area-of-concern'
          ),
        ),
    ),
    languageSelection: route(
      routeNames.LANGUAGE_SELECTION,
      'language-selection',
      'appointments.wpo_language_selection.title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-language-selection" */ './containers/language-selection/language-selection'
          ),
        ),
    ),
    timeslotSelection: route(
      routeNames.TIMESLOT_SELECTION,
      'timeslot-selection',
      'appointments.timeslot_selection.title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-timeslot-selection" */ './containers/timeslot-selection/timeslot-selection'
          ),
        ),
    ),
    counsellorSelection: route(
      routeNames.COUNSELLOR_SELECTION,
      'counsellor-selection',
      'appointments.counselor_selection.title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-counsellor-selection" */ './containers/counsellor-selection/counsellor-selection'
          ),
        ),
    ),
    appointmentType: route(
      routeNames.APPOINTMENT_TYPE,
      'appointment-type',
      'appointments.appointment_type.title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-appointment-type" */ './containers/appointment-type/appointment-type'
          ),
        ),
    ),
    appointmentConfirmation: route(
      routeNames.APPOINTMENT_CONFIRMATION,
      'appointment-confirmation',
      'appointments.appointment_confirmation.title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-appointment-confirmation" */ './containers/appointment-confirmation/appointment-confirmation'
          ),
        ),
    ),
    bookingConfirmation: route(
      routeNames.BOOKING_CONFIRMATION,
      'booking-confirmation',
      'wpo_booking.module.confirmation_page_title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-booking-confirmation" */ './containers/booking-confirmation/booking-confirmation'
          ),
        ),
    ),
    appointmentDetails: route(
      routeNames.APPOINTMENT_DETAILS,
      'appointment-details',
      'wpo_booking.module.confirmation_page_title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-appointment-details" */ './containers/appointment-details/appointment-details'
          ),
        ),
    ),
    appointmentCancelled: route(
      routeNames.APPOINTMENT_CANCELLED,
      'appointment-details/cancelled',
      'wpo_booking.module.confirmation_page_title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-appointment-cancelled" */ './containers/appointment-cancelled/appointment-cancelled'
          ),
        ),
    ),
    appointmentHistory: route(
      routeNames.APPOINTMENT_HISTORY,
      'appointments',
      'appointments.view_appointments.title',
      () =>
        createResolver(
          import(
            /* webpackChunkName: "wpo-appointments-appointment-history" */ './containers/appointment-history/appointment-history'
          ),
        ),
    ),
  };
};
